import {DestroyRef, inject, Injectable, InjectionToken} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import type {ShepherdService} from 'angular-shepherd';
import {LanguageService} from '../i18n/language.service';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {LoggingService} from './logging.service';

export interface TourStep {
    id: string;
    title?: string;
    text?: string;
    attachTo?: {
        element: string;
        on: 'top' | 'bottom' | 'left' | 'right' | 'auto' | 'auto-start' | 'auto-end' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'right-start' | 'right-end' | 'left-start' | 'left-end';
    };
    buttons?: Array<{
        text: string;
        action: () => void;
        classes?: string;
    }>;
    when?: {
        show?: () => void;
        hide?: () => void;
        cancel?: () => void;
        complete?: () => void;
        destroy?: () => void;
    };
    classes?: string;
    scrollTo?: boolean | ScrollIntoViewOptions;
    [key: string]: any;
}

export interface Tour {
    id: string;
    steps: TourStep[];
}

export interface TourStartOptions {
    scrollTo?: boolean | ScrollIntoViewOptions;
}

/**
 * Líný loader shepherd.js. `angular-shepherd` (a s ním shepherd.js, ~55 KB raw) se stahuje
 * až při prvním spuštění průvodce — `TourService` injektuje topbar, takže statický import
 * by knihovnu držel v initial bundle na každém startu. Token kvůli testům.
 */
export const SHEPHERD_LOADER = new InjectionToken<() => Promise<ShepherdService>>('SHEPHERD_LOADER', {
    providedIn: 'root',
    factory: () => () => import('angular-shepherd').then(m => new m.ShepherdService()),
});

@Injectable({
    providedIn: 'root'
})
export class TourService {
    private readonly loadShepherdImpl = inject(SHEPHERD_LOADER);
    private readonly languageService = inject(LanguageService);
    private readonly http = inject(HttpClient);
    private readonly translateService = inject(TranslateService);
    private readonly logger = inject(LoggingService);
    private readonly destroyRef = inject(DestroyRef);

    private tourTranslations: Record<string, unknown> = {};
    /** Jazyk, pro který jsou překlady průvodce načtené; `null` = zatím žádný průvodce neběžel. */
    private translationsLanguage: string | null = null;
    private currentLanguage: string;
    private toursCache: Map<string, Tour> = new Map();
    private tourActive$ = new BehaviorSubject<boolean>(false);

    private shepherd: ShepherdService | null = null;
    private shepherdLoading: Promise<ShepherdService> | null = null;

    constructor() {
        this.currentLanguage = this.languageService.getCurrentLanguageSnapshot().code;

        // Překlady průvodce se nestahují při startu aplikace, jen při změně jazyka poté,
        // co už nějaký průvodce běžel (jinak by každý start platil request navíc).
        this.languageService.getLanguage()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(language => {
                this.currentLanguage = language.code;
                if (this.translationsLanguage === null) {
                    return;
                }
                void this.loadTourTranslations(language.code).then(() => {
                    // Běžící průvodce přeložit znovu
                    const currentTourId = this.shepherd?.isActive ? this.shepherd.tourObject?.id : undefined;
                    if (currentTourId) {
                        this.shepherd!.cancel();
                        void this.startTour(currentTourId);
                    }
                });
            });
    }

    private loadShepherd(): Promise<ShepherdService> {
        if (!this.shepherdLoading) {
            this.shepherdLoading = this.loadShepherdImpl().then(instance => {
                this.shepherd = instance;
                return instance;
            });
        }
        return this.shepherdLoading;
    }

    /**
     * Load tour translations for specified language
     */
    private async loadTourTranslations(languageCode: string): Promise<void> {
        try {
            const translations = await firstValueFrom(
                this.http.get<Record<string, unknown>>(`./assets/i18n/tour/${languageCode}.json`)
            );
            this.tourTranslations = translations || {};
            this.translationsLanguage = languageCode;
        } catch (error) {
            this.logger.error(`Failed to load tour translations for language: ${languageCode}`, error);
            this.tourTranslations = {};
        }
    }

    private async ensureTranslations(): Promise<void> {
        if (this.translationsLanguage === this.currentLanguage) {
            return;
        }
        await this.loadTourTranslations(this.currentLanguage);
    }

    /**
     * Get translation for a tour key
     */
    private getTranslation(key: string): string {
        const keys = key.split('.');
        let value: unknown = this.tourTranslations;

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = (value as Record<string, unknown>)[k];
            } else {
                return key; // Return key if translation not found
            }
        }

        return typeof value === 'string' ? value : key;
    }

    private configure(shepherd: ShepherdService, scrollTo: boolean | ScrollIntoViewOptions): void {
        shepherd.defaultStepOptions = {
            cancelIcon: {
                enabled: true
            },
            classes: 'shepherd-theme-custom',
            scrollTo,
            canClickTarget: false  // Prevent clicking on highlighted elements
        };
        shepherd.modal = true;
        shepherd.confirmCancel = false;
    }

    /**
     * Start a tour by ID
     */
    async startTour(tourId: string): Promise<void> {
        const tour = this.toursCache.get(tourId);

        if (!tour) {
            this.logger.error(`Tour with id "${tourId}" not found in startTour`);
            return;
        }

        const [shepherd] = await Promise.all([this.loadShepherd(), this.ensureTranslations()]);

        // Translate tour steps
        const translatedSteps = tour.steps.map(step => ({
            ...step,
            title: step.title ? this.getTranslation(step.title) : undefined,
            text: step.text ? this.getTranslation(step.text) : undefined,
            buttons: step.buttons?.map(button => ({
                ...button,
                // Use TranslateService for buttons to access main translations (common.buttons.*)
                text: this.translateService.instant(button.text)
            }))
        }));

        this.configure(shepherd, {behavior: 'auto', block: 'center'});
        shepherd.addSteps(translatedSteps);
        shepherd.start();
        this.tourActive$.next(true);

        // Log tour start
        this.logger.info('TOUR_EVENT: Started tour', {
            tourId: tourId
        });

        // Listen for tour completion
        shepherd.tourObject?.once('complete', () => {
            this.tourActive$.next(false);

            // Log tour completion
            this.logger.info('TOUR_EVENT: Completed tour', {
                tourId: tourId
            });
        });

        shepherd.tourObject?.once('cancel', () => {
            this.tourActive$.next(false);

            // If user cancelled on the last step, log it as completed
            const currentStep = shepherd.tourObject?.getCurrentStep();
            const allSteps = shepherd.tourObject?.steps;
            if (currentStep && allSteps && allSteps.length > 0) {
                const currentStepIndex = allSteps.indexOf(currentStep);
                const isLastStep = currentStepIndex === allSteps.length - 1;

                if (isLastStep) {
                    this.logger.info('TOUR_EVENT: Completed tour', {
                        tourId: tourId,
                        completedVia: 'cancel-on-last-step'
                    });
                }
            }
        });
    }

    /**
     * Ad-hoc průvodce z připravených kroků (bez registrace a bez překladů průvodce) —
     * topbar takhle ukazuje „průvodce není dostupný".
     */
    async startSteps(steps: TourStep[], options: TourStartOptions = {}): Promise<void> {
        const shepherd = await this.loadShepherd();
        if (shepherd.isActive) {
            shepherd.cancel();
        }
        this.configure(shepherd, options.scrollTo ?? {behavior: 'auto', block: 'center'});
        shepherd.addSteps(steps);
        shepherd.start();
        this.tourActive$.next(true);
        shepherd.tourObject?.once('complete', () => this.tourActive$.next(false));
        shepherd.tourObject?.once('cancel', () => this.tourActive$.next(false));
    }

    /**
     * Register a tour
     */
    registerTour(tour: Tour): void {
        this.toursCache.set(tour.id, tour);
    }

    /** Běží právě teď nějaký průvodce? Bez načteného shepherdu vždy false. */
    isActive(): boolean {
        return this.shepherd?.isActive ?? false;
    }

    /** Zruší běžící průvodce; bez načteného shepherdu no-op. */
    cancel(): void {
        if (this.shepherd?.isActive) {
            this.shepherd.cancel();
            this.tourActive$.next(false);
        }
    }

    /** Dokončí běžící průvodce (tlačítko „Zavřít" v ad-hoc kroku). */
    complete(): void {
        this.shepherd?.complete();
    }

    /**
     * Check if a tour is currently active
     */
    isTourActive(): boolean {
        return this.tourActive$.value;
    }

    /**
     * Get tour active state as observable
     */
    getTourActiveState() {
        return this.tourActive$.asObservable();
    }
}
